import {
  useHeaderFloorsQuery,
  useHeaderRoomQuery,
  useHeaderZoneQuery,
  useHeaderBuildingsQuery,
  SensorStatusFragment,
} from '@/.generated/graphql';
import { PageLayoutHeader } from '@/components/PageLayout';
import { useSRZId } from '@/hooks';
import { getSingleGraphQLModel } from '@/utils/getSingleGraphQLModel';
import { gql } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { UserMenu } from './UserMenu';
import { useMemo, useCallback } from 'react';
import { Icon } from '@/components/primitives/icon/Icon';
import { Button } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { IconName } from '@/components/primitives/icon/generated/iconNames';
import { TagContainer } from '@/components/Tags/TagContainer';
import { useFeatureGate } from '@statsig/react-bindings';
import dayjs from 'dayjs';
import { useTimezone } from '@/hooks';
import { useDetailsPanel } from '@/contexts';
import { datadogRum } from '@datadog/browser-rum';

interface HeaderProps {}

export const Header: React.FC<HeaderProps> & { queries?: any } = () => {
  const { isOpen, close, open } = useDetailsPanel();
  const url = useLocation();
  const { roomId, spaceId, zoneId, buildingId } = useSRZId();
  const { data: spaceData, loading: spaceLoading } = useHeaderFloorsQuery({
    // skip validates this assertion.

    variables: { spaceId: spaceId! },
    skip: !spaceId,
  });

  const { data: buildingData } = useHeaderBuildingsQuery({
    // skip validates this assertion.

    variables: { buildingId: buildingId! },
    skip: !buildingId,
  });

  const space = getSingleGraphQLModel(spaceData?.floors);
  const skip = !spaceId || !roomId;
  const { data: roomData, loading: roomLoading } = useHeaderRoomQuery({
    // asserted by skip below

    variables: { roomId: roomId! },
    skip,
  });
  const { data: zoneData, loading: zoneLoading } = useHeaderZoneQuery({
    // asserted by skip below

    variables: { zoneId: zoneId! },
    skip: !zoneId,
  });

  const { value: tagging } = useFeatureGate('tagging');

  const { room, zone, associatedTags } = useMemo(() => {
    const room = roomData?.rooms?.data?.[0];
    const zone = zoneData?.zones?.data?.[0];
    const associatedTags =
      roomId || zoneId
        ? roomId
          ? roomData?.rooms?.data?.[0]?.tags
          : zoneData?.zones?.data?.[0]?.tags
        : spaceData?.floors?.data?.[0]?.tags;

    return { room, zone, associatedTags };
  }, [roomData, zoneData, spaceData, roomId, zoneId]);

  const { headerName, iconName } = useMemo(() => {
    if (spaceLoading || roomLoading || zoneLoading) {
      return { headerName: '', iconName: 'layer' as IconName };
    }

    const headerName = buildingData?.building?.name
      ? buildingData.building.name
      : url.pathname === '/studio'
      ? space?.name
      : roomId && room
      ? room.name
      : zoneId && zone
      ? zone.name
      : spaceId && space
      ? space.name
      : '';

    const iconName: IconName =
      url.pathname === '/studio'
        ? 'layer'
        : roomId && room
        ? 'room'
        : zoneId && zone
        ? 'zone'
        : spaceId && space
        ? 'layer'
        : buildingId
        ? 'building'
        : 'layer';

    return { headerName: headerName || '', iconName };
  }, [
    spaceLoading,
    roomLoading,
    zoneLoading,
    buildingData?.building?.name,
    url.pathname,
    space?.name,
    roomId,
    room?.name,
    zoneId,
    zone?.name,
    spaceId,
    buildingId,
  ]);
  const associateId = zoneId || roomId || spaceId;
  const isLoading = spaceLoading || roomLoading || zoneLoading;

  const queryToRefetch = useMemo(() => {
    const queries = [];
    if (zoneId) {
      queries.push({
        query: Header.queries.Zone,
        variables: { zoneId },
      });
    } else if (roomId) {
      queries.push({
        query: Header.queries.Room,
        variables: { roomId },
      });
    } else if (spaceId && (!roomId || !zoneId)) {
      queries.push({
        query: Header.queries.Floors,
        variables: { spaceId },
      });
    }
    return queries;
  }, [zoneId, roomId, spaceId]);

  const sensors = roomId ? room?.sensors : zoneId ? zone?.sensors : space?.sensors;
  const tz = useTimezone();
  const nowTime = dayjs().tz(tz).format('HH:mm');
  const hiveConfig = space?.hives?.[0]?.config?.frame_rate;
  const sensorStatus = useMemo(() => {
    if (spaceLoading || roomLoading || zoneLoading) return null;
    return getStatus(nowTime, tz, sensors ?? [], hiveConfig);
  }, [nowTime, sensors, hiveConfig, spaceLoading, roomLoading, zoneLoading]);

  const handleToggle = useCallback(() => {
    datadogRum.addAction('Toggle Details Panel/Settings', {
      source: 'settings-and-details',
      action: isOpen ? 'close' : 'open',
    });
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  return (
    <PageLayoutHeader className="z-[9] bg-surface pt-2">
      <div className="flex flex-wrap items-center gap-4 text-2xl font-semibold py-1 justify-between w-full">
        {headerName && (
          <div className="flex items-center truncate" data-id="header-name-icon">
            <Icon name={iconName} className="mr-2 h-7 w-7 mt-[2px]" />
            {headerName}
            {location.pathname !== '/devices' &&
              location.pathname !== '/studio' &&
              !isLoading &&
              tagging &&
              !buildingId && (
                <div className="ml-4">
                  <TagContainer
                    associatedTags={associatedTags?.map((tag) => tag.name) ?? []}
                    itemId={associateId}
                    queryToRefetch={queryToRefetch}
                  />
                </div>
              )}
          </div>
        )}
        {!buildingId && (
          <div className="flex items-center gap-2">
            {sensorStatus !== null && (
              <span className="text-content-secondary text-sm font-normal" data-id="sensor-status">
                <span
                  className={
                    sensorStatus === 'ACTIVE'
                      ? 'text-content-primary'
                      : sensorStatus === 'INACTIVE'
                      ? 'text-status-error'
                      : 'text-status-asleep'
                  }
                >
                  Sensor{' '}
                  {sensorStatus === SensorStatus.ACTIVE
                    ? 'Active'
                    : sensorStatus === SensorStatus.INACTIVE
                    ? 'Inactive'
                    : 'Asleep'}
                </span>
                <span className="ml-1">in this {roomId ? 'room' : zoneId ? 'zone' : 'space'}</span>
              </span>
            )}
            <Button
              data-id="showHideDetails"
              icon={<SettingOutlined />}
              size="small"
              onClick={handleToggle}
            />
          </div>
        )}
      </div>
    </PageLayoutHeader>
  );
};
const SENSOR_FRAGMENT = gql`
  fragment SensorStatus on Sensor {
    id
    name
    is_streaming
  }
`;

Header.queries = {
  Buildings: gql`
    query HeaderBuildings($buildingId: ID!) {
      building(id: $buildingId) {
        id
        name
      }
    }
  `,
  Floors: gql`
    query HeaderFloors($spaceId: String!) {
      floors(ids: [$spaceId]) {
        data {
          id: floor_id
          name
          tags {
            id
            name
          }
          sensors {
            ...SensorStatus
          }
          hives {
            serial_number
            config {
              frame_rate {
                monday
                tuesday
                wednesday
                thursday
                friday
                saturday
                sunday
              }
            }
          }
          ...UserMenuFloor
        }
      }
    }
    ${SENSOR_FRAGMENT}
    ${UserMenu.fragments.Floor}
  `,
  Room: gql`
    query HeaderRoom($roomId: String!) {
      rooms(ids: [$roomId]) {
        data {
          id: room_id
          name
          tags {
            id
            name
          }
          sensors {
            ...SensorStatus
          }
        }
      }
    }
    ${SENSOR_FRAGMENT}
  `,
  //can only get tags on zones, not rooms or spaces yet
  Zone: gql`
    query HeaderZone($zoneId: String!) {
      zones(ids: [$zoneId]) {
        data {
          id: zone_id
          name
          tags {
            id
            name
          }
          sensors {
            ...SensorStatus
          }
        }
      }
    }
    ${SENSOR_FRAGMENT}
  `,
};

export enum SensorStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ASLEEP = 'ASLEEP',
}
/**
 * Determines the operational status of sensors based on frame rate configuration
 * @param nowTime Current time in 24-hour format (HH:mm)
 * @param sensors Array of sensors to check
 * @param hiveConfig Hive frame rate configuration with day-specific operation hours
 * @returns Status: "ACTIVE", "INACTIVE", or "ASLEEP"
 */

export function getStatus(
  nowTime: string,
  tz: string,
  sensors: SensorStatusFragment[] | undefined,
  hiveConfig?: {
    monday?: string;
    tuesday?: string;
    wednesday?: string;
    thursday?: string;
    friday?: string;
    saturday?: string;
    sunday?: string;
  },
): SensorStatus {
  if (!sensors?.length || !hiveConfig) {
    return SensorStatus.INACTIVE;
  }
  const allSensorsOffline = sensors.every((sensor) => sensor.is_streaming === false);
  if (allSensorsOffline) {
    return SensorStatus.INACTIVE;
  }
  const now = dayjs().tz(tz);
  const currentDay = now.format('dddd').toLowerCase();
  const hours = hiveConfig[currentDay as keyof typeof hiveConfig];

  if (hours) {
    const [startHour, endHour] = hours.split(',');
    const startTime = parseInt(startHour);
    const endTime = parseInt(endHour);
    const currentHour = parseInt(nowTime.split(':')[0]);

    const isWithinOperatingHours = currentHour >= startTime && currentHour < endTime;

    if (!isWithinOperatingHours) {
      return SensorStatus.ASLEEP;
    }
  }

  return SensorStatus.ACTIVE;
}
